import { useEffect, useRef } from 'react';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';

import { cn } from '#/lib/utils';

interface HeadshotProps {
  name: string;
  title: string;
  headshotUrl: string;
  className?: string;
  shouldAnimate?: boolean;
  additionalMobileDelay?: number;
}

export function Headshot({
  name,
  title,
  headshotUrl,
  className,
  shouldAnimate = false,
  additionalMobileDelay = 0,
}: HeadshotProps) {
  const textContainerRef = useRef<HTMLDivElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<GSAPTimeline>(null);

  useGSAP(() => {
    const mm = gsap.matchMedia();

    if (imageContainerRef.current && textContainerRef.current) {
      // Set initial states
      gsap.set(imageContainerRef.current, {
        width: 0,
        height: 0,
      });

      gsap.set(textContainerRef.current, {
        y: 10,
        opacity: 0,
      });

      // Desktop timeline (768px and above)
      mm.add('(min-width: 768px)', () => {
        timelineRef.current = gsap
          .timeline({ paused: true, delay: 1 })
          .to(imageContainerRef.current, {
            width: 110,
            height: 110,
            duration: 0.7,
          })
          .to(
            textContainerRef.current,
            {
              y: 0,
              opacity: 1,
              duration: 0.8,
            },
            0,
          ); // Start at the same time as image animation
      });

      // Large phone
      mm.add('(min-width: 400px) and (max-width: 767px)', () => {
        timelineRef.current = gsap
          .timeline({ paused: true, delay: 1.7 + additionalMobileDelay })
          .to(imageContainerRef.current, {
            width: 85,
            height: 85,
            duration: 0.5,
          })
          .to(
            textContainerRef.current,
            {
              y: 0,
              opacity: 1,
              duration: 0.6,
            },
            0,
          ); // Start at the same time as image animation
      });
      // small phone
      mm.add('(max-width: 399px)', () => {
        timelineRef.current = gsap
          .timeline({ paused: true, delay: 1.7 + additionalMobileDelay })
          .to(imageContainerRef.current, {
            width: 65,
            height: 65,
            duration: 0.5,
          })
          .to(
            textContainerRef.current,
            {
              y: 0,
              opacity: 1,
              duration: 0.6,
            },
            0,
          ); // Start at the same time as image animation
      });
    }
  }, []);

  useEffect(() => {
    if (shouldAnimate && timelineRef.current) {
      timelineRef.current.play();
    }
  }, [shouldAnimate]);

  return (
    <div className={cn(`flex items-center gap-8 m:gap-12 flex-col`, className)}>
      <div className="m:size-110 size-65 s:size-85  flex items-center justify-center">
        <div ref={imageContainerRef} className="rounded-full overflow-hidden">
          <img src={headshotUrl} alt={name} />
        </div>
      </div>
      <div
        ref={textContainerRef}
        className="font-[500] font-planar flex flex-col text-center bg-white rounded-[8px] m:rounded-[12px] shadow-[0_4.359px_34.173px_5.231px_rgba(95,91,247,0.12)] py-6 m:px-30 px-15 m:min-w-[130px] min-w-[80px]"
      >
        <span className="text-10 s:text-14 m:text-17 text-charcoal">
          {name}
        </span>
        <span className="text-8 s:text-10 m:text-10 text-grayscale-04">
          {title}
        </span>
      </div>
    </div>
  );
}
